import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import type IBondDetails from "../../interfaces/IBondDetails";
import type ICalculatedValues from "../../interfaces/ICalculatedValues";
import { IAnalysisReport, INews } from "./interfaces";
import type {
  ICashflow,
  UserDetails,
} from "../../components/RFQOrders/interfaces";
import { generateBondQuotationFirstPage } from "./generateBondQuotationFirstPage";
import { generateBondQuotationSecondPage } from "./generateBondQuotationSecondPage";
import { generateBondQuotationThirdPage } from "./generateBondQuotationThirdPage";
import { generateBondQuotationFourthPage } from "./generateBondQuotationFourthPage";
import { addSmestWatermarkToAllPages } from "./addWatermark";
import type { ILiquidityIndicator } from "./liquidityIndicator";

// Adapter: your store uses different field names than the PDF fn expects
const adaptCalculated = (cv: ICalculatedValues) => {
  const totalConsideration = (cv?.investmentAmount ?? 0) - (cv?.stampDuty ?? 0);
  // settlementDate is a moment in your store – convert to Date
  const settlementDate =
    // @ts-ignore moment
    typeof cv?.settlementDate?.toDate === "function"
      ? // @ts-ignore moment
        cv.settlementDate.toDate()
      : cv?.settlementDate;

  return {
    price: cv?.price,
    yield: cv?.Yield,
    count: cv?.noOfBonds,
    principal: cv?.principalAmount,
    accruedInterest: cv?.accruedInterest,
    stampDuty: cv?.stampDuty,
    totalConsideration,
    settlementDate,
  };
};

// Adapter: your bondInfo sometimes has faceValue instead of pricePerBond
const adaptBond = (bondInfo: IBondDetails) => ({
  ...bondInfo,
  // fall back to faceValue if pricePerBond is absent
  pricePerBond:
    // @ts-ignore allow both shapes
    (bondInfo as any).pricePerBond ?? (bondInfo as any).faceValue ?? "",
});

export async function createQuotationPDF(
  bondInfo: IBondDetails,
  calculatedValues: ICalculatedValues,
  userDetails: UserDetails,
  cashflow: ICashflow[],
  analysisReport: IAnalysisReport,
  news: INews[],
  liquidityIndicator?: ILiquidityIndicator
) {
  const doc = new jsPDF("p", "pt", "a4");
  await generateBondQuotationFirstPage(
    doc,
    adaptBond(bondInfo) as any,
    adaptCalculated(calculatedValues) as any,
    userDetails
  );

  // Page 2 (cashflows). We explicitly add a new page.
  doc.addPage("a4");
  const { nextY: nextY2 } = await generateBondQuotationSecondPage(
    doc,
    adaptBond(bondInfo) as any,
    { count: (calculatedValues as any)?.noOfBonds },
    cashflow
  );

  // Page 3 (report sections) — start exactly after the cashflow disclaimer block
  const { nextY: nextY3 } = await generateBondQuotationThirdPage(
    doc,
    adaptBond(bondInfo) as any,
    adaptCalculated(calculatedValues) as any,
    userDetails,
    analysisReport,
    news,
    { startY: nextY2 }
  );

  // Page 4 (revenue/profit line chart) — starts right after page 3 content
  await generateBondQuotationFourthPage(
    doc,
    adaptBond(bondInfo) as any,
    adaptCalculated(calculatedValues) as any,
    userDetails,
    analysisReport,
    news,
    { startY: nextY3, liquidityIndicator }
  );

  // Add diagonal logo watermark on every page (after all content)
  addSmestWatermarkToAllPages(doc, {
    opacity: 0.06,
    widthFactor: 0.55,
    angleDeg: 45,
  });

  // Option A: download
  const preparedForName =
    (userDetails as any)?.name?.trim() ||
    [userDetails.firstName, userDetails.lastName].filter(Boolean).join(" ");
  doc.save(`${preparedForName}_${bondInfo?.securityName}.pdf`);

  // Option B: preview in new tab (comment A, uncomment B)
  // const url = doc.output("bloburl");
  // window.open(url, "_blank", "noopener,noreferrer");
}
